import React, { useEffect, useRef } from 'react';
import { useAutosave } from '../hooks/useAutosave';
import { useEditor } from '../context/EditorContext';
import { useIsSmallScreen } from '../hooks/useIsSmallScreen';
import { useBillingNotices } from '../hooks/useBillingNotices';
import { RibbonBar } from '../components/ribbon/RibbonBar';
import { LeftSidebar } from '../components/editor/LeftSidebar';
import { RightSidebar } from '../components/editor/RightSidebar';
import { CanvasWorkspace } from '../components/editor/CanvasWorkspace';
import { FindReplacePane } from '../components/editor/FindReplacePane';
import { MeasurementPane } from '../components/editor/MeasurementPane';
import { BottomBar } from '../components/editor/BottomBar';
import { DialogHost } from '../components/dialogs/DialogHost';
import { PhotoEditorModal } from '../components/modals/PhotoEditorModal';
import { ImageGalleryModal } from '../components/modals/ImageGalleryModal';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { getProject, getSharedProject, apiErrorMessage } from '../lib/projectApi';
import { touchRecent } from '../lib/recents';
import { toast } from 'sonner';

export default function Editor() {
  const { state, dispatch, rawDispatch } = useEditor();
  useAutosave();
  useBillingNotices();
  const vo = state.viewOptions || {};
  const small = useIsSmallScreen();

  // Links into the editor:
  //   /app?share=<token>  — File ▸ Share link. The owner gets the original; anyone else gets their own copy (unsaved until
  //                         they save, then it lives in their account). The owner's original is never opened for editing.
  //   /app?project=<id>   — the owner's own saved publication (recents / older links).
  // The parameter is removed once handled so a later refresh does not re-open it over newer work.
  const linkHandled = useRef(false);
  useEffect(() => {
    if (linkHandled.current) return;
    linkHandled.current = true;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('project');
    const shareToken = params.get('share');
    if (!id && !shareToken) return;
    (async () => {
      try {
        if (shareToken) {
          const shared = await getSharedProject(shareToken);
          if (shared.is_owner) {
            dispatch({ type: 'SET_PROJECT', payload: shared });
            touchRecent(shared.id);
            toast.success(`Opened “${shared.name}”`, { description: 'This is your own shared publication.' });
          } else {
            dispatch({ type: 'SET_PROJECT', payload: { ...shared, id: null, thumbnail: null, created_at: null, updated_at: null } });
            toast.success(`Opened “${shared.name}” shared by ${shared.shared_by}`, { description: 'This is your own copy — save it to keep it in your publications.', duration: 6000 });
          }
        } else {
          const full = await getProject(id);
          dispatch({ type: 'SET_PROJECT', payload: full });
          touchRecent(full.id);
          toast.success(`Opened “${full.name}”`);
        }
      } catch (e) {
        toast.error(await apiErrorMessage(e, shareToken ? 'This publication is no longer shared, or the link is not valid.' : 'That publication could not be found in your account.'));
      } finally {
        params.delete('project');
        params.delete('share');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      }
    })();
  }, [dispatch]);

  // On small screens the side panes are hidden by default; the View tab can still open them (as overlays).
  const initialised = useRef(false);
  useEffect(() => {
    if (small && !initialised.current) {
      initialised.current = true;
      rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key: 'pageNavigation', value: false } });
      rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key: 'propertiesPane', value: false } });
    }
  }, [small, rawDispatch]);

  const showPages = vo.pageNavigation !== false;
  const showProperties = vo.propertiesPane !== false;
  const closePane = (key) => rawDispatch({ type: 'SET_VIEW_OPTION', payload: { key, value: false } });

  return (
    <div className="editor-layout" data-testid="editor-layout">
      {/* Publisher-style ribbon (File tab opens the Backstage) */}
      <RibbonBar />

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative min-h-0">
        {/* Page Navigation pane */}
        {showPages && !small && <LeftSidebar />}

        {/* Canvas Area with Rulers + the Find and Replace task pane floating over it */}
        <div className="flex-1 flex min-w-0 min-h-0 relative">
          <CanvasWorkspace />
          <FindReplacePane />
          <MeasurementPane />
        </div>

        {/* Properties / Layers pane */}
        {showProperties && !small && <RightSidebar />}
      </div>

      {/* Small screens: panes become slide-over sheets */}
      {small && (
        <>
          <Sheet open={showPages} onOpenChange={(o) => !o && closePane('pageNavigation')}>
            <SheetContent side="left" className="w-[200px] p-0 bg-card [&>button]:hidden" data-testid="pages-sheet"><LeftSidebar /></SheetContent>
          </Sheet>
          <Sheet open={showProperties} onOpenChange={(o) => !o && closePane('propertiesPane')}>
            <SheetContent side="right" className="w-[300px] p-0 bg-card [&>button]:hidden" data-testid="properties-sheet"><RightSidebar /></SheetContent>
          </Sheet>
        </>
      )}

      {/* Status bar */}
      <BottomBar />

      {/* Dialogs & Modals */}
      <DialogHost />
      {state.showPhotoEditor && <PhotoEditorModal />}
      {state.showImageGallery && <ImageGalleryModal />}
    </div>
  );
}
